import React, { useEffect, useState } from "react";
import { MdClose, MdCheck } from "react-icons/md";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { getTagVoteCounts, submitTagVote, getUserVote } from "@/services/TagsService";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import useLocalStorage from "@/hooks/useLocalStorage";
import { useUser } from "@/context/UserContext";
import { useAuth0 } from "@auth0/auth0-react";

interface TagShape {
    id: string;
    name: string;
}

interface TagVoteBarProps {
    tag: TagShape;
    taggableId: string;
    tagId: string;
    userId: string;
    hideVoteButtons?: boolean;
    propertyId: string;
}

const TagVoteBar: React.FC<TagVoteBarProps> = ({ tag, taggableId, tagId, userId, hideVoteButtons, propertyId }) => {
    const queryClient = useQueryClient();
    const [voteCounts, setVoteCounts] = useState<{ agree: number; disagree: number }>({
        agree: 0,
        disagree: 0,
    });
    const [userVote, setUserVote] = useState<"agree" | "disagree" | "none" | null>(null);
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const [socialState] = useLocalStorage<any>("social-state", {});
    const { userState } = useUser();
    const { isAuthenticated } = useAuth0();
    const isListingPage = window.location.pathname.includes("/listing/");

    const refetchQueries = () => {
        (queryClient as any).invalidateQueries(["tag-votes", taggableId, tagId]);
        (queryClient as any).invalidateQueries(["user-vote", taggableId, tagId, userId]);
        (queryClient as any).invalidateQueries(["comments", propertyId, tagId]);
    };

    const { data: voteCountsData, isLoading: isVotesLoading } = useQuery({
        queryKey: ["tag-votes", taggableId, tagId, userId],
        queryFn: () => getTagVoteCounts(taggableId, tagId),
        enabled: Boolean(taggableId && tagId && userId),
    });

    const { data: userVoteData, isLoading: isUserVoteLoading } = useQuery({
        queryKey: ["user-vote", taggableId, tagId, userId],
        queryFn: () => getUserVote(taggableId, tagId, userId),
        enabled: Boolean(taggableId && tagId && userId),
    });

    // Update local states when server data changes
    useEffect(() => {
        if ((voteCountsData as any)?.data) {
            setVoteCounts({
                agree: (voteCountsData as any).data.agree || 0,
                disagree: (voteCountsData as any).data.disagree || 0,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(voteCountsData as any)?.data]);

    useEffect(() => {
        if ((userVoteData as any)?.voteType) {
            setUserVote((userVoteData as any).voteType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(userVoteData as any)?.voteType]);

    const voteMutation = useMutation({
        mutationFn: ({ voteType }: { voteType: "agree" | "disagree" | "none" }) =>
            submitTagVote(taggableId, tagId, userId, voteType),
        onMutate: async (newVote) => {
            const newVoteCounts = { ...voteCounts };

            // Update counts based on vote change
            if (userVote && userVote !== "none") (newVoteCounts as any)[userVote]--;
            if (newVote.voteType !== "none") (newVoteCounts as any)[newVote.voteType]++;

            // Optimistic update
            setUserVote(newVote.voteType);
            setVoteCounts(newVoteCounts);
        },
        onError: (err: any) => {
            toast.error(err.message);
            refetchQueries();
        },
        onSuccess: () => {
            refetchQueries();

            // Track Vote Hashtag event on successful vote
            let pageName = "Property Feed - Trending";
            if (isListingPage) {
                pageName = "Property Details";
            } else if ((socialState as any)?.activeTab === "following") {
                pageName = "Property Feed - Following";
            }

            (sendMixpanelEvent as any)("Vote Hashtag", {
                pageName,
                hashtag: tag.name,
                user_id: userId,
                nickname: (userState as any)?.nickname,
                email: (userState as any)?.emailAddress,
                isAuthenticated,
            });
        },
    });

    const handleVote = async (voteType: "agree" | "disagree" | "none") => {
        (voteMutation as any).mutate({ voteType });
    };

    if (!taggableId || !tagId) {
        return null;
    }

    if (userId && (isVotesLoading || isUserVoteLoading)) {
        return (
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-mt-2">
                <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-text-[18px] tw-font-semibold tw-w-full tw-relative">
                    <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-24"></div>
                    <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-32"></div>
                    <div className="tw-h-6 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-16"></div>
                </div>
                <div className="tw-flex tw-gap-2 tw-mt-2 tw-w-full">
                    <div className="tw-flex-1 tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                    <div className="tw-flex-1 tw-h-10 tw-bg-gray-200 tw-rounded-lg tw-animate-pulse"></div>
                </div>
                <div className="tw-w-full tw-mt-3 tw-mb-1">
                    <div className="tw-w-full tw-h-2 tw-bg-gray-200 tw-rounded-full tw-animate-pulse"></div>
                </div>
                <div className="tw-flex tw-items-center tw-justify-between tw-w-full">
                    <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-24"></div>
                    <div className="tw-h-4 tw-bg-gray-200 tw-rounded tw-animate-pulse tw-w-24"></div>
                </div>
            </div>
        );
    }

    const total = voteCounts.disagree + voteCounts.agree;
    const noPercent = total > 0 ? (voteCounts.disagree / total) * 100 : 0;
    const yesPercent = total > 0 ? (voteCounts.agree / total) * 100 : 0;

    return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-1 tw-mt-2">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-center tw-gap-2 tw-text-[18px] tw-font-semibold tw-w-full tw-relative">
                {hideVoteButtons ? (
                    <span className="tw-text-[#6C26C4]">#{tag.name}</span>
                ) : (
                    <>
                        <span>Do you agree with the</span>
                        <span className="tw-text-[#6C26C4]">#{tag.name}</span>
                        <span>tag?</span>
                    </>
                )}
            </div>
            {!hideVoteButtons && (
                <div className="tw-flex tw-gap-2 tw-mt-2 tw-w-full">
                    <button
                        className={
                            userVote === "disagree"
                                ? "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#DB2528] tw-text-white tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2"
                                : "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#F3F4F6] tw-text-black tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2 tw-border-[1px] tw-border-solid tw-border-[#D9D9D9]"
                        }
                        onClick={() => handleVote("disagree")}
                        disabled={(voteMutation as any).isPending}
                    >
                        <MdClose
                            className={
                                userVote === "disagree" ? "tw-text-white tw-text-xl" : "tw-text-black tw-text-xl"
                            }
                        />{" "}
                        Nope
                    </button>
                    <button
                        className={
                            userVote === "agree"
                                ? "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#55A809] tw-text-white tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2"
                                : "tw-flex-1 tw-flex tw-items-center tw-justify-center tw-bg-[#F3F4F6] tw-text-black tw-font-bold tw-py-2 tw-rounded-lg tw-text-base tw-gap-2 tw-border-[1px] tw-border-solid tw-border-[#D9D9D9]"
                        }
                        onClick={() => handleVote("agree")}
                        disabled={(voteMutation as any).isPending}
                    >
                        <MdCheck
                            className={userVote === "agree" ? "tw-text-white tw-text-xl" : "tw-text-black tw-text-xl"}
                        />{" "}
                        Yep
                    </button>
                </div>
            )}
            {userId && (
                <>
                    <div className="tw-w-full tw-mt-3 tw-mb-1">
                        <div className="tw-w-full tw-h-2 tw-bg-gray-200 tw-rounded-full tw-relative">
                            <div
                                className="tw-bg-[#DB2528] tw-h-2 tw-rounded-l-full tw-transition-all tw-duration-500 tw-ease-in-out"
                                style={{ width: `${noPercent}%` }}
                            />
                            <div
                                className="tw-bg-[#55A809] tw-h-2 tw-rounded-r-full tw-absolute tw-top-0 tw-transition-all tw-duration-500 tw-ease-in-out"
                                style={{ left: `${noPercent}%`, width: `${yesPercent}%` }}
                            />
                        </div>
                    </div>
                    <div className="tw-flex tw-items-center tw-justify-between tw-w-full tw-text-xs tw-font-normal tw-italic tw-mb-2 tw-text-black">
                        <span>
                            {voteCounts.disagree.toLocaleString()} <span className="tw-font-bold not-italic">NO</span>{" "}
                            votes
                        </span>
                        <span>
                            {voteCounts.agree.toLocaleString()} <span className="tw-font-bold not-italic">YES</span>{" "}
                            votes
                        </span>
                    </div>
                </>
            )}
        </div>
    );
};

export default TagVoteBar;
